import { LabMessageDTO } from "@/Common/Api/Labs/Dto/Responses/LabMessageGroupDTO";
import { BaseReactComponent } from "@/Common/Components/BaseReactComponent";
import { ButtonControl } from "@/Common/Components/Buttons/ButtonControl";
import { FeatureFlagControl } from "@/Common/Components/FeatureFlagControl";
import { CheckboxControl } from "@/Common/Components/Inputs/CheckboxControl";
import { SpanControl } from "@/Common/Components/SpanControl";
import { CommonTableHeaderComponent } from "@/Common/Components/Tables/CommonTableHeaderComponent";
import { MyleTable } from "@/Common/Components/Tables/MyleTable";
import { MyleTableColumn, MyleTableColumnOptions } from "@/Common/Components/Tables/MyleTableColumn";
import { MyleTableColumnOrderControlv2 } from "@/Common/Components/Tables/MyleTableColumnOrderControlv2";
import { SimplePagerControl } from "@/Common/Components/Tables/SimplePagerControl";
import { DateHelper } from "@/Common/Helpers/DateHelper";
import { EmployeeTitleHelper } from "@/Common/Helpers/EmployeeTitleHelper";
import { Patient } from "@/Common/Models/Patient/Patient";
import { LabInboxes, LabSortOrderType } from "@/Common/Models/UI/Enums";
import { ITableRenderOptions } from "@/Common/Models/UI/Tables/ITableRenderOptions";
import { MyleTableTarget } from "@/Common/Models/UI/Tables/MyleTableTarget";
import * as LocalOperations from "@/Common/Operations/operations.local";
import * as ServerOperations from "@/Common/Operations/operations.server";
import { LabMessageTableItem } from "../Models/LabMessageTableItem";
import { ILabsInboxModel } from "../Models/UI/ILabsInboxModel";
import { LabClinicianTableCell } from "./LabClinicianTableCell";
import { LabReportCombinedStatusComponent } from "./LabReportCombinedStatusComponent";
import { LabReportReadingStatusComponent } from "./LabReportReadingStatusComponent";
import { LabsInboxSettingsContainer } from "./LabsInboxSettingsContainer";
import { ManualLabsInboxSettingsContainer } from "./ManualLabsInboxSettingsContainer";

interface ILabsInboxViewProps {
    Model: ILabsInboxModel;
    PatientContactId?: string;
    PatientModel?: Patient;
    IsDSQActive?: boolean;
}

class LabsInboxView extends BaseReactComponent<ILabsInboxViewProps> {
    private helper = cacheStore.Helper;
    public selectedTableItemIDs: string[];

    public render(): JSX.Element {
        const canFileAndHold = sessionStore.canApproveDocument();

        // prepare The Grid
        const SaveOptionKey = "LabGridSettings"; // Unique Key that will be used to Append/Load your Grid Settings in the DataBase in a JSON format to [dbo].[Login].[UserSettings]

        const columnsOptions = this.helper.loadTableColumnOptionsFromSettings(SaveOptionKey, this.GetMyleTableColumnsDefaultOptions() /* Default Options */);
        const tableColumns = this.getTableColumns(columnsOptions); // Add Rendering Functions
        const tableRenderOptions = this.loadTableRenderOptions(columnsOptions); // Table Options
        const columnConfigModel = this.helper.castColumnConfigurationToMyleTableColumnOptions(columnsOptions.toArray());
        const currentInboxIsManual = this.props.Model.get("CurrentInbox") === LabInboxes.ManualResults;
        this.scanForSelectedItemsInTheTable();

        return (
            <div className="workspace">
                <CommonTableHeaderComponent
                    title={this.ResolveCurrentInboxName(labsStore.LabsInboxModel.get("CurrentInbox"))}
                    searchText={this.props.Model.get("GlobalLabsearchText")}
                    searchPlaceholder={this.props.Model.get("GlobalLabsearchPlaceholder")}
                    onSearch={text => sessionStore.dispatch(LocalOperations.Labs.SearchBoxTextChanged, { value: text })}
                    onRefreshClick={() => sessionStore.dispatch(LocalOperations.Labs.OnRefreshClick)}
                    onSettingsClick={() => sessionStore.dispatch(LocalOperations.Labs.LabInboxSearchSettings_ShowOptionsButtonClick)}
                />

                <div id="posref" className="ws-scrollable">
                    {this.props.Model.get("LabInboxDisplaySettingsOption") && (
                        <div onClick={this.onContainerClick} className="filter-component filter-component-responsive" id="labs_search_options_container">
                            <FeatureFlagControl
                                flag={cacheStore.ClinicSettings.EnableManualEntryOfLabResults && cacheStore.ClinicSettings.EnableCentralizedManualLabsGrid && currentInboxIsManual}
                                fallbackFeature={<LabsInboxSettingsContainer LabViewModel={this.props.Model} />}
                                activeFeature={(
                                    <ManualLabsInboxSettingsContainer
                                        filterSettings={labsStore.getManualLabsInboxFilteringConfiguration()}
                                        onOkClick={nextSettings => {
                                            labsStore.setManualLabsInboxFilteringConfiguration(nextSettings);
                                            sessionStore.dispatch(LocalOperations.Labs.LabInboxSearchSettings_OnOkClick);
                                        }}
                                        onCancelClick={() => sessionStore.dispatch(LocalOperations.Labs.LabInboxSearchSettings_OnCancelClick)}
                                    />
                                )}
                            />
                        </div>
                    )}

                    <div className="myle-doc-tb main-content table-wrapper">
                        <MyleTable
                            TableRenderOptions={tableRenderOptions}
                            TableId="labinbox_main_tb"
                            Columns={tableColumns}
                            DataSource={this.props.Model.get("LabsTableData")}
                            ColumnsPreferencesChangedOperation={ServerOperations.Server.Myle_UpdateUserSettings}
                            RowClickOperation={LocalOperations.Labs.Table_InboxRowClick}
                            SaveOptionskeyTarget={SaveOptionKey}
                            ColumnSortTarget={MyleTableTarget.Myle_LabInbox_Search_ColumnSortClick}
                        />
                    </div>
                    <div className="clearfix" />
                </div>

                <div className="ws-footer table-pager-footer">
                    <div className="row">
                        <div className="col-lg-6 col-md-6 col-sm-6">
                            <FeatureFlagControl
                                flag={cacheStore.ClinicSettings.EnableManualEntryOfLabResults && cacheStore.ClinicSettings.EnableCentralizedManualLabsGrid && currentInboxIsManual}
                                activeFeature={this.renderManualLabsPaginationControl()}
                                fallbackFeature={(
                                    <SimplePagerControl
                                        PageSize={this.props.Model.get("SelectedPageSizeOption")}
                                        TotalRows={this.props.Model.get("TotalQueryRows")}
                                        CurrentPage={this.props.Model.get("CurrentPage")}
                                        EventToDispatchOnPageChange={LocalOperations.Labs.CurrentPageChange}
                                    />
                                )}
                            />

                        </div>
                        {canFileAndHold && (
                            <div className="col-lg-6 col-md-6 col-sm-6 text-right">
                                {!this.isManualLabInbox() && (
                                    <ButtonControl
                                        className="btn btn-primary push-right-5"
                                        label={cacheStore.GetString("OnHoldLabAction")}
                                        disabled={!(this.selectedTableItemIDs.length > 0)}
                                        onClick={() => sessionStore.dispatch(
                                            LocalOperations.Labs.Inbox_ButtonClick_HoldLabs,
                                            { item: this.selectedTableItemIDs }
                                        )}
                                    />
                                )}
                                {(!this.isManualLabInbox() || (this.isManualLabInbox() && EmployeeTitleHelper.isDoctor(sessionStore.myleEmployeeTitle))) && (
                                    <ButtonControl
                                        variant="brand"
                                        label={cacheStore.GetString("FileLabAction")}
                                        disabled={this.selectedTableItemIDs.length <= 0}
                                        onClick={() => { this.handleFileLabResults(); }}
                                    />
                                )}
                            </div>
                        )}
                    </div>
                </div>

                {this.props.Model.get("ColumnOrderingPopupOpen") && (
                    <MyleTableColumnOrderControlv2
                        TableColumns={columnConfigModel}
                        SaveOptionskeyTarget={SaveOptionKey}
                        ContainerId="columnSettingsPopup"
                        CloseColumOrder={LocalOperations.Labs.LabInboxColumnOrderingPopup_Close}
                    />
                )}
            </div>
        );
    }

    public componentDidMount(): void {
        labsStore.rebindCurrentEmployee();

        $(window).trigger("resize");
    }

    private onContainerClick(e: React.MouseEvent): void {
        e.stopPropagation();
    }

    public scanForSelectedItemsInTheTable(): void {
        // Compute SelectedTableItemIDs
        this.selectedTableItemIDs = [];
        this.props.Model.get("LabsTableData")
            .forEach(item => {
                if (item.IsSelected) {
                    this.selectedTableItemIDs.push(item.Model.Model.LabFillerID);
                }
            });
    }

    private GetMyleTableColumnsDefaultOptions(): MyleTableColumnOptions[] {
        if (cacheStore.ClinicSettings.EnableResultsFixes) {
            return [
                new MyleTableColumnOptions({
                    HeaderText: "",
                    FieldName: "LabCheckBox",
                    WidthPixels: 38
                }),
                new MyleTableColumnOptions({
                    HeaderText: this.getLabClinicianHeaderText(),
                    FieldName: "LabClinician",
                    RowClickEnabled: true,
                    WidthPercentageDefault: 8
                }),
                new MyleTableColumnOptions({
                    HeaderText: cacheStore.GetString("PatientLabel"),
                    FieldName: "LabPatient",
                    RowClickEnabled: true,
                    WidthPercentageDefault: 8
                }),
                new MyleTableColumnOptions({
                    HeaderText: cacheStore.GetString("SendingFacilityLabel"),
                    FieldName: "SendingFacility",
                    RowClickEnabled: true,
                    WidthPercentageDefault: 12
                }),
                new MyleTableColumnOptions({
                    HeaderText: cacheStore.GetString("ContextLabel"),
                    FieldName: "Context",
                    RowClickEnabled: true,
                    WidthPercentageDefault: 12
                }),
                new MyleTableColumnOptions({
                    HeaderText: cacheStore.GetString("MessageDate"),
                    FieldName: "LabMessageDate",
                    RowClickEnabled: true,
                    IsSortable: true,
                    SortColumn: LabSortOrderType.MessageDate,
                    SortDirection: labsStore.LabsInboxModel.get("SortColumnDirection"),
                    IsSortActive: true,
                    WidthPercentageDefault: 17.1
                }),
                new MyleTableColumnOptions({
                    HeaderText: cacheStore.GetString("DescriptionLabel"),
                    FieldName: "MessageSummary",
                    RowClickEnabled: true,
                    WidthPercentageDefault: 12
                }),
                new MyleTableColumnOptions({
                    HeaderText: cacheStore.GetString("StatusLabel"),
                    FieldName: "Status",
                    RowClickEnabled: true,
                    WidthPercentageDefault: 12
                }),
                new MyleTableColumnOptions({
                    HeaderText: cacheStore.GetString("DetailsLabel"),
                    FieldName: "Detail",
                    RowClickEnabled: true,
                    WidthPercentageDefault: 12
                }),
                new MyleTableColumnOptions({
                    HeaderText: cacheStore.GetString(cacheStore.ClinicSettings.EnableResultsFixes ? "ActionLabel" : "NotesDLabel"),
                    FieldName: "Action",
                    WidthPercentageDefault: 5,
                    RowClickEnabled: false
                })
            ];
        }

        return [
            new MyleTableColumnOptions({
                HeaderText: "",
                FieldName: "LabCheckBox",
                WidthPixels: 38
            }),
            new MyleTableColumnOptions({
                HeaderText: this.getLabClinicianHeaderText(),
                FieldName: "LabClinician",
                RowClickEnabled: true,
                WidthPercentageDefault: 8
            }),
            new MyleTableColumnOptions({
                HeaderText: cacheStore.GetString("PatientLabel"),
                FieldName: "LabPatient",
                RowClickEnabled: true,
                WidthPercentageDefault: 8
            }),
            new MyleTableColumnOptions({
                HeaderText: cacheStore.GetString("SendingFacilityLabel"),
                FieldName: "SendingFacility",
                RowClickEnabled: true,
                WidthPercentageDefault: 12
            }),
            new MyleTableColumnOptions({
                HeaderText: cacheStore.GetString("ContextLabel"),
                FieldName: "Context",
                RowClickEnabled: true,
                WidthPercentageDefault: 12
            }),
            new MyleTableColumnOptions({
                HeaderText: cacheStore.GetString("MessageDate"),
                FieldName: "LabMessageDate",
                RowClickEnabled: true,
                IsSortable: true,
                SortColumn: LabSortOrderType.MessageDate,
                SortDirection: labsStore.LabsInboxModel.get("SortColumnDirection"),
                IsSortActive: true,
                WidthPercentageDefault: 17.1
            }),
            new MyleTableColumnOptions({
                HeaderText: cacheStore.GetString("DescriptionLabel"),
                FieldName: "MessageSummary",
                RowClickEnabled: true,
                WidthPercentageDefault: 12
            }),
            new MyleTableColumnOptions({
                HeaderText: cacheStore.GetString("StatusLabel"),
                FieldName: "Status",
                RowClickEnabled: true,
                WidthPercentageDefault: 12
            }),
            new MyleTableColumnOptions({
                HeaderText: cacheStore.GetString(cacheStore.ClinicSettings.EnableResultsFixes ? "ActionLabel" : "NotesDLabel"),
                FieldName: "Action",
                WidthPercentageDefault: 5,
                RowClickEnabled: false
            })
        ];
    }

    private getTableColumns(columnsOptions: Immutable.List<MyleTableColumnOptions>): Immutable.List<MyleTableColumn> {
        const tableCellClass = "text-wrap-control";

        return MyleTableColumn.buildList<LabMessageTableItem>(
            columnsOptions,
            column => {
                switch (column.FieldName) {
                    case "LabCheckBox":
                        return {
                            renderCell: item => (
                                <div>
                                    <CheckboxControl
                                        id={item.Model.Model.LabFillerID}
                                        checked={item.IsSelected}
                                        operation={LocalOperations.Labs.CheckLabMessageItem}
                                        item={item}
                                    />
                                </div>
                            ),
                            renderHeader: () => (
                                <div className="checkbox-col">
                                    <CheckboxControl
                                        checked={this.props.Model.get("SelectAllRowItemsChecked")}
                                        className="header-input"
                                        operation={LocalOperations.Labs.CheckAllLabMessageItem}
                                    />
                                </div>
                            )
                        };

                    case "LabClinician":
                        if (labsStore.LabsInboxModel.get("CurrentInbox") === LabInboxes.ManualResults) {
                            return {
                                renderCell: item => (
                                    <LabClinicianTableCell
                                        clinicians={item.Model.Model.IsManualLabEntry ? [item.Model.getPrescriptorDTO(), ...item.Model.getCarbonCopiesCDTO()] : [item.Model.getPrescriptorDTO()]}
                                        isRed={item.Model.IsAbnormal}
                                        isBold={item.Model.Model.IsUnreadForQueryingClinician}
                                        cellClassName={tableCellClass}
                                    />
                                )
                            };
                        }
                        else {
                            return {
                                renderCell: item => (
                                    <div title={item.Model.ClinicianPrescriptorName}>
                                        <SpanControl
                                            IsRed={item.Model.IsAbnormal}
                                            IsBold={item.Model.Model.IsUnreadForQueryingClinician}
                                            className={tableCellClass}
                                            text={item.Model.ClinicianPrescriptorName}
                                        />
                                    </div>
                                )
                            };
                        }

                    case "LabPatient":
                        return {
                            renderCell: item => (
                                <div title={item.Model.PatientName}>
                                    <SpanControl
                                        IsRed={item.Model.IsAbnormal}
                                        IsBold={item.Model.Model.IsUnreadForQueryingClinician}
                                        className={tableCellClass}
                                        text={item.Model.PatientName}
                                    />
                                </div>
                            )
                        };

                    case "SendingFacility":
                        return {
                            renderCell: item => (
                                <div title={item.Model.Model.SendingFacility}>
                                    <SpanControl
                                        IsRed={item.Model.IsAbnormal}
                                        IsBold={item.Model.Model.IsUnreadForQueryingClinician}
                                        className={tableCellClass}
                                        text={item.Model.Model.SendingFacility}
                                    />
                                </div>
                            )
                        };

                    case "Context":
                        return {
                            renderCell: item => (
                                <div title={item.Model.Model.Context}>
                                    <SpanControl
                                        IsRed={item.Model.IsAbnormal}
                                        IsBold={item.Model.Model.IsUnreadForQueryingClinician}
                                        className={tableCellClass}
                                        text={item.Model.Model.Context}
                                    />
                                </div>
                            )
                        };

                    case "LabMessageDate":
                        return {
                            renderCell: item => {
                                const emittedDate = DateHelper.formatDate(item.Model.Model.EmittedDateTime, "YYYY/MM/DD") || "";
                                return (
                                    <div title={emittedDate} className={item.Model.IsAbnormal ? "red-row" : ""}>
                                        <SpanControl
                                            IsRed={item.Model.IsAbnormal}
                                            IsBold={item.Model.Model.IsUnreadForQueryingClinician}
                                            className={tableCellClass}
                                            text={emittedDate}
                                        />
                                    </div>
                                );
                            }
                        };

                    case "MessageSummary":
                        return {
                            renderCell: item => (
                                <div title={item.Model.Model.RequestSubjects}>
                                    <SpanControl
                                        IsRed={item.Model.IsAbnormal}
                                        IsBold={item.Model.Model.IsUnreadForQueryingClinician}
                                        className={tableCellClass}
                                        text={item.Model.Model.RequestSubjects}
                                    />
                                </div>
                            )
                        };

                    case "Status":
                        return {
                            renderCell: item => {
                                let StatusValue = item.Model.Model.ResolvedLabStatus;
                                if (StatusValue == "ClassifiedByCollege")
                                    StatusValue = cacheStore.GetString("ClassifiedByCollegeLabel");
                                else if (StatusValue == "SubmittedByCollege")
                                    StatusValue = cacheStore.GetString("SubmittedByCollege");
                                else if (StatusValue == "Corrected")
                                    StatusValue = cacheStore.GetString("CorrectedLabel");
                                else if (StatusValue == "Classified")
                                    StatusValue = cacheStore.GetString("ClassifiedLabel");
                                else if (StatusValue == "Submitted")
                                    StatusValue = cacheStore.GetString("OnHoldLabsInbox");
                                else if (StatusValue == "New")
                                    StatusValue = cacheStore.GetString("NewLabel");

                                return (
                                    <div title={StatusValue}>
                                        <FeatureFlagControl
                                            flag={cacheStore.ClinicSettings.EnableLabResultProcessingStatus}
                                            activeFeature={<LabReportCombinedStatusComponent LabMessage={item.Model} />}
                                            fallbackFeature={<LabReportReadingStatusComponent LabMessage={item.Model} />}
                                        />
                                    </div>
                                );
                            }
                        };

                    case "Detail":
                        if (cacheStore.ClinicSettings.EnableResultsFixes) {
                            return {
                                renderCell: item => (
                                    <div title={item.Model.Model.Detail}>
                                        <SpanControl
                                            IsRed={item.Model.IsAbnormal}
                                            IsBold={item.Model.Model.IsUnreadForQueryingClinician}
                                            className={tableCellClass}
                                            text={item.Model.Model.Detail}
                                        />
                                    </div>
                                )
                            };
                        }

                        return { renderCell: () => (null) };
                    case "Action":
                        return {
                            renderCell: item => {
                                let notesArray = item.Model.Model.ParentLevel_MyleNoteDtos;
                                let actionsArray = item.Model.Model.ParentLevel_MyleActionDtos;

                                let globalnotes = notesArray ? notesArray.filter(x => String.prototype.isNullOrWhiteSpace(x.ObservationId)) : [];
                                let obxnotes = notesArray ? notesArray.filter(x => !String.prototype.isNullOrWhiteSpace(x.ObservationId)) : [];
                                let actions = actionsArray ? actionsArray : [];

                                return (
                                    <div className="flex-container" style={{ justifyContent: "flex-start", width: "80px" }}>
                                        {globalnotes.length > 0 &&
                                            <a style={{ marginLeft: "0px" }} onClick={e => this.sendActionNoteOperation(item.Model.Model, e)}>{"[ "}<span className="red-text">{globalnotes.length}</span>{" ]"}</a>
                                        }
                                        {globalnotes.length == 0 &&
                                            <a style={{ marginLeft: "0px" }} onClick={e => this.sendActionNoteOperation(item.Model.Model, e)}>[ + ]</a>
                                        }
                                        {(obxnotes.length > 0 || actions.length > 0) &&
                                            <a style={{ marginLeft: "6px" }} onClick={() => this.popupObxActionWarning()}><span style={{ color: "red", position: "absolute", marginTop: "-4px", fontSize: "20px" }}>{" ! "}</span></a>
                                        }
                                    </div>
                                );
                            }
                        };
                }
            },
            false
        );
    }

    public loadTableRenderOptions(columnOptions: Immutable.List<MyleTableColumnOptions>): ITableRenderOptions {
        // Get The Render Options
        const defaultTablePluginOptions = {
            MinColWidth: 60,
            MinTableWidth: 980,
            Cols: columnOptions.filter(x => x.IsHidden === false).map(i => i.WidthPercentage),
            FirstColumnNoDrag: true,
            LastColumnNoDrag: true,
            Autoresize: true,
            OnColumnWidthChange: null
        };
        return Immutable.fromJS(defaultTablePluginOptions);
    }

    public ResolveCurrentInboxName(LabInbox: LabInboxes): string {
        if (LabInbox == LabInboxes.All)
            return cacheStore.GetString("AllLabInbox"); // "All Lab Inbox";
        if (LabInbox == LabInboxes.New)
            return cacheStore.GetString("NewLabsInbox");
        if (LabInbox == LabInboxes.Suspended)
            return cacheStore.GetString("OnHoldLabsInbox");
        if (LabInbox == LabInboxes.Classified)
            return cacheStore.GetString("FiledLabsInbox");
        if (LabInbox == LabInboxes.ManualResults)
            return cacheStore.GetString("ManualResults_ManualResultsLabel");
    }

    private sendActionNoteOperation(lab: LabMessageDTO, e: React.MouseEvent): void {
        const request = {
            labFillerId: lab.LabFillerID,
            observation: null,
            notes: lab.ParentLevel_MyleNoteDtos ? lab.ParentLevel_MyleNoteDtos.filter(x => String.prototype.isNullOrWhiteSpace(x.ObservationId)) : [],
            event: e
        };

        if (cacheStore.ClinicSettings.EnableResultsFixes) {
            sessionStore.dispatch(
                LocalOperations.LabsUI.ResultDetailClick,
                {
                    ...request,
                    detail: lab.Detail,
                    isManualLabEntry: lab.IsManualLabEntry
                }
            );

            return;
        }

        sessionStore.dispatch(
            LocalOperations.LabsUI.NotesClick,
            request
        );
    }

    private popupObxActionWarning(): void {
        sessionStore.showPopup({
            title: cacheStore.GetString("AdditionalinformationLabel"),
            content: cacheStore.GetString("AdditionalNotesLabel")
        });
    }

    private getLabClinicianHeaderText(): string {
        if (cacheStore.ClinicSettings.EnableManualEntryOfLabResults && cacheStore.ClinicSettings.EnableCentralizedManualLabsGrid && this.isManualLabInbox()) {
            return cacheStore.GetString("DoctorsLabel");
        }
        return cacheStore.GetString("PrescribedByLabel");
    }

    private isManualLabInbox(): boolean {
        return labsStore.LabsInboxModel.get("CurrentInbox") === LabInboxes.ManualResults;
    }

    private handleFileLabResults(): void {
        if (this.isManualLabInbox()) {
            sessionStore.dispatch(LocalOperations.Labs.Inbox_ButtonClick_FileManualLabs, { item: this.selectedTableItemIDs });
            return;
        }
        sessionStore.dispatch(LocalOperations.Labs.Inbox_ButtonClick_FileLabs, { item: this.selectedTableItemIDs });
    }

    private renderManualLabsPaginationControl(): JSX.Element {
        const filterSettings = labsStore.getManualLabsInboxFilteringConfiguration();

        return (
            <SimplePagerControl
                PageSize={filterSettings.pageSize}
                TotalRows={filterSettings.totalRows}
                CurrentPage={filterSettings.pageNumber}
                changePageFunction={pageNumber => {
                    labsStore.setManualLabsInboxFilteringConfiguration({ ...filterSettings, pageNumber });
                    labsStore.LabActions.RefreshLabs();
                }}
            />
        );
    }
}

export { ILabsInboxViewProps, LabsInboxView };
